/**
 * One report's card on `/reports` (POS2-1137 CP1).
 *
 * The Preview button arrived in CP2 with the screen behind it, and the toggle in CP4 with the
 * lifecycle behind it — §4's "no route reachable before the screen behind it works" is what set
 * that order, because a dead control is worse than an absent one. A never-configured report gets
 * the dark "Preview & set up" rather than the quiet "Preview", because for that card previewing is
 * the only way it can ever be switched on.
 *
 * **A never-configured report's toggle opens the editor rather than switching anything on.** A
 * report can never go live unseen, and refusing at the switch would be a control that will not
 * move; sending the person to the screen that *can* answer them is the version that respects the
 * click. Same for a report paused because everyone was removed: resuming it would send to nobody.
 *
 * **A viewer who may not configure gets the third shape: a disabled switch (POS2-1313).** The rule
 * above — send them somewhere that can answer — assumes there is an answer. For a read-only role
 * there is none, so the link becomes a control that looks live and leads nowhere, and the button
 * beside it stops saying "& set up" for the same reason. The three cases are "you have not yet",
 * "you cannot until you fix something", and "not you", and only the first two share a destination.
 *
 * The states a card can be in are the states a schedule row can be in, and every one of them is
 * **read** rather than computed here — `pause_reason` separates a person's pause from the
 * schedule pausing itself, `bounced_emails` comes from `report_deliveries`, `last_change` from the
 * audit log. A browser deriving any of those would be reading tables it has no business knowing
 * about (decision 12).
 *
 * **The foot is the button, the label and the switch — the mock's row, minus its pill.** That pill
 * reads "{n} pages · PDF", and a count is a fact about a payload — `pageCount(payload, enabledIds)`
 * — which this card has not fetched; the preview states it. "Delivered as a PDF by email" stood in
 * the slot for a while, which the page subtitle already says once, so four cards repeated it — and
 * at 26 characters it wrapped the row. The mock's foot never wraps, and a wrapping one moves the
 * switch every time the sidebar collapsing into the grid changes the card's width. The On/Off
 * label carries the mock's fixed width for the same reason.
 */
import { AlertTriangle, Check, Clock, Eye } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { useTranslation } from "@/i18n/use-translation";
import type { Locale } from "@/i18n/config";
import { formatDate, formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";
import { avatarTint, firstName, initials } from "@/lib/report-recipients";
import {
  liveRecipients,
  runOutcome,
  sentAt,
  type Cadence,
  type ReportRun,
  type ReportSchedule,
} from "@/hooks/use-reports";
import type { CatalogueEntry } from "@/lib/report-catalogue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

/** A lookup table of literals, never a built key: `check-i18n.mjs` resolves keys statically and a
 * key assembled at runtime is invisible to it — reported as dead and deleted by the next sweep. */
const SCHEDULE_KEYS: Record<Cadence, string> = {
  weekdays: "reports.schedule.weekdays",
  every_day: "reports.schedule.everyDay",
  weekly: "reports.schedule.weekly",
  first_of_month: "reports.schedule.firstOfMonth",
  last_friday: "reports.schedule.lastFriday",
};

interface Props {
  entry: CatalogueEntry;
  schedule: ReportSchedule | null;
  /** The newest send of this report type, or null if it has never gone out. */
  lastRun: ReportRun | null;
  timezone: string;
  busy: boolean;
  /** Null when the toggle should open the editor instead of flipping anything. */
  onToggle: ((paused: boolean) => void) | null;
  /**
   * Whether this viewer may change what sends (POS2-1313). Separate from `onToggle` being null,
   * because the two mean opposite things about what to do next: a null toggle on a schedule paused
   * for want of a recipient is *fixable*, and the preview is where you fix it — which is why that
   * case is a link. Somebody without the permission has nothing to fix, so the same link reads as a
   * working control and leads to a dead end. Seen in use, not reasoned about in advance.
   */
  canConfigure: boolean;
}

export function ReportCard({
  entry,
  schedule,
  lastRun,
  timezone,
  busy,
  onToggle,
  canConfigure,
}: Props) {
  const { locale, t } = useTranslation();
  const Icon = entry.icon;
  const recipients = schedule ? liveRecipients(schedule) : [];
  const running = !!schedule && !schedule.is_paused;

  // "Every Monday at 07:00" — its own sentence rather than the PDF header's "Arrives every…",
  // because the card carries an "Arrives" label and would otherwise say the verb twice.
  const scheduleSentence = schedule
    ? t(SCHEDULE_KEYS[schedule.cadence], {
        // Wire JSON is "07:00:00"; the hour the site set, never a UTC offset.
        time: schedule.send_time_local.slice(0, 5),
        weekday: schedule.send_dow == null ? "" : weekdayName(locale, schedule.send_dow),
      })
    : "";

  return (
    <article className="flex h-full flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-700">
          <Icon className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900">
            {t(entry.titleKey)}
            <Badge variant="secondary" className="font-normal">
              {t(entry.frequencyKey)}
            </Badge>
          </h3>
          <p className="mt-1 text-sm text-gray-500">{t(entry.descriptionKey)}</p>
        </div>
      </div>

      <dl className="flex flex-col gap-2 border-t border-gray-100 pt-3 text-sm">
        <Row label={t("reports.card.arrives")}>
          {!schedule ? (
            <span className="text-gray-400">{t("reports.card.notScheduled")}</span>
          ) : schedule.is_paused ? (
            <span className="text-gray-400">
              {/* Two pauses, two sentences. "Everyone was removed" is not something the customer
                  did, so a card that said "Paused · was every Monday" would read as somebody
                  having flipped a switch they did not flip. */}
              {schedule.pause_reason === "no_recipients"
                ? t("reports.card.pausedNoRecipients")
                : t("reports.card.paused", { schedule: scheduleSentence })}
            </span>
          ) : (
            <span className="text-gray-700">{scheduleSentence}</span>
          )}
        </Row>

        <Row label={t("reports.card.goesTo")}>
          {recipients.length === 0 ? (
            <span className="text-gray-400">{t("reports.card.nobodyYet")}</span>
          ) : (
            <span className="flex items-center gap-2">
              <span className="flex -space-x-1.5">
                {recipients.slice(0, 4).map((r) => (
                  <span
                    key={r.id}
                    className={`flex size-6 items-center justify-center rounded-full text-[10px] font-semibold ring-2 ring-white ${avatarTint(r.email, !r.user_id)}`}
                    title={r.email}
                  >
                    {initials(r.email)}
                  </span>
                ))}
              </span>
              <span className="truncate text-gray-700">{recipientNames(t, recipients)}</span>
            </span>
          )}
        </Row>

        {/* Quiet, under the avatars, and it persists until the address is fixed — a bounce is a
            fact about a mailbox, not an event to toast once and lose. */}
        {schedule && schedule.bounced_emails.length > 0 && (
          <p className="flex items-start gap-1.5 rounded bg-orange-50 px-2 py-1.5 text-xs text-orange-700">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            {t("reports.card.bounced", { email: schedule.bounced_emails[0] })}
          </p>
        )}

        <Row label={t("reports.card.lastOne")}>
          {lastRun ? (
            <LastRunState run={lastRun} locale={locale} timezone={timezone} />
          ) : (
            <span className="text-gray-400">{t("reports.card.neverSent")}</span>
          )}
        </Row>
      </dl>

      {schedule?.last_change && (
        <p className="text-xs text-gray-400">
          {schedule.last_change.actor_name
            ? t("reports.card.lastChangedBy", {
                name: schedule.last_change.actor_name,
                when: formatRelative(locale, schedule.last_change.at),
              })
            : t("reports.card.lastChangedAutomatically", {
                when: formatRelative(locale, schedule.last_change.at),
              })}
        </p>
      )}

      {/* The foot holds its place in two directions, and each needed its own fix (POS2-1279).
          *Across* the row: `ml-auto` rather than a zero-basis `<span className="grow" />`, which
          only holds the right edge while the row fits on one line — "Vorschau & einrichten", 21
          characters against "Preview"'s 7, wrapped the row in the `xl` 3-up column and left the
          switch *left*-aligned on a second line. The button is now the element that gives.
          *Down* the card: `mt-auto` against the article's `h-full`. Grid stretches the cards to
          one height, but the content stacks from the top, so a three-line description (Monthly,
          in English as much as in German) pushed its foot below the two-line cards' and the four
          buttons sat at four heights. The auto margin puts every foot on the card's floor. */}
      <div className="mt-auto flex items-center gap-3">
        <Button asChild size="sm" variant={schedule ? "outline" : "default"} className="min-w-0">
          <Link to={`/waste/reports/${entry.key}`}>
            <Eye className="size-4 shrink-0" aria-hidden="true" />
            <span className="truncate">
              {/* "& set up" is an invitation, and for a read-only viewer it is one the next screen
                  does not keep — there is no editor there for them. They get the honest half. */}
              {t(
                schedule || !canConfigure ? "reports.card.preview" : "reports.card.previewAndSetUp",
              )}
            </span>
          </Link>
        </Button>
        <div className="ml-auto flex shrink-0 items-center gap-3">
          {/* A fixed width, as the mock's `.tgl-lbl` has: "Off" is wider than "On", and
              without it flipping a report nudges the switch beside it. */}
          <span className={cn("w-7 text-xs", running ? "text-gray-900" : "text-gray-400")}>
            {t(running ? "reports.card.on" : "reports.card.off")}
          </span>
          {!canConfigure ? (
            // **Here a disabled switch is right, where below it is not.** The objection to one is
            // that it says nothing about why — but that objection assumes there is something the
            // viewer could do about it. There is not: the answer is "not you", which `disabled`
            // plus the title says exactly, and the state stays readable rather than being replaced
            // by a control that promises a destination.
            <Switch
              checked={running}
              disabled
              aria-label={t("reports.card.toggle", { report: t(entry.titleKey) })}
              title={t("reports.card.toggleReadOnly")}
            />
          ) : onToggle ? (
            <Switch
              checked={running}
              disabled={busy}
              onCheckedChange={(next) => onToggle(!next)}
              aria-label={t("reports.card.toggle", { report: t(entry.titleKey) })}
            />
          ) : (
            // Not a disabled switch: a control that will not move says nothing about why. This one
            // goes to the screen that can answer — the preview, where it can be set up or its
            // recipient list refilled.
            <Link
              to={`/waste/reports/${entry.key}`}
              role="switch"
              aria-checked={false}
              aria-label={t("reports.card.toggle", { report: t(entry.titleKey) })}
              className="inline-flex h-[1.15rem] w-8 shrink-0 items-center rounded-full border border-transparent bg-gray-200 p-px shadow-xs"
            >
              <span className="block size-4 rounded-full bg-white ring-0" />
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

/** POS2-1324 — the tick used to appear for any run at all, over a timestamp that silently fell
 * back to the generation time. Three states, because a report that has not been sent is either
 * still waiting for its collection window or is never going out, and an operator asking "did it
 * go?" needs those to look different from each other and from success.
 */
function LastRunState({
  run,
  locale,
  timezone,
}: {
  run: ReportRun;
  locale: Locale;
  timezone: string;
}) {
  const { t } = useTranslation();
  const outcome = runOutcome(run);

  if (outcome === "sent") {
    return (
      <span className="flex flex-wrap items-center gap-1.5 text-gray-700">
        <Check className="size-3.5 text-emerald-600" aria-hidden="true" />
        {t("reports.card.sent")}
        <span className="text-gray-400">{sentAt(locale, run, timezone)}</span>
      </span>
    );
  }

  if (outcome === "failed") {
    return (
      <span className="flex flex-wrap items-center gap-1.5 text-red-700">
        <AlertTriangle className="size-3.5 shrink-0" aria-hidden="true" />
        {t("reports.card.sendFailed")}
        <span className="text-gray-400">
          {t("reports.card.generatedAt", { time: sentAt(locale, run, timezone) })}
        </span>
      </span>
    );
  }

  // Waiting: the PDF exists and the email has not left. Neutral rather than a warning — this is
  // the normal state for the half hour a report is held (POS2-1312), not a fault.
  return (
    <span className="flex flex-wrap items-center gap-1.5 text-gray-700">
      <Clock className="size-3.5 shrink-0 text-gray-400" aria-hidden="true" />
      {t("reports.card.waitingToSend")}
      <span className="text-gray-400">
        {t("reports.card.generatedAt", { time: sentAt(locale, run, timezone) })}
      </span>
    </span>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="w-20 shrink-0 text-xs text-gray-400">{label}</dt>
      <dd className="min-w-0 flex-1">{children}</dd>
    </div>
  );
}

/** "Aoife & Tomás" up to two, "Aoife + 3 more" past it — the mock's rule. */
function recipientNames(
  t: (key: string, params?: Record<string, string | number>) => string,
  recipients: { email: string }[],
): string {
  if (recipients.length === 1) return firstName(recipients[0].email);
  if (recipients.length === 2) {
    return t("reports.card.recipientsPair", {
      first: firstName(recipients[0].email),
      second: firstName(recipients[1].email),
    });
  }
  return t("reports.card.recipientsMore", {
    first: firstName(recipients[0].email),
    n: recipients.length - 1,
  });
}

/** `send_dow` is 0 = Monday, and 2024-01-01 was a Monday. `Intl` rather than seven catalogue keys
 * per locale (POS2-1176): a weekday name is formatting, and a hand-written table goes stale for
 * every locale added later. */
function weekdayName(locale: Locale, dow: number): string {
  return formatDate(locale, new Date(Date.UTC(2024, 0, 1 + dow)), {
    weekday: "long",
    timeZone: "UTC",
  });
}
